import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, FolderPlus, Layers, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { RequestRecoveryDialog } from '@/features/objects/components/RequestRecoveryDialog'
import { useRecoverySets } from '../../hooks'
import { useGroupRequest, useImpactAssessments, useToRequest, type ToRequestLine } from '../../impact-assessment'
import { RecoveryPlanPanel } from './RecoveryPlanPanel'

const problemOf = (error: unknown) =>
    (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error
    || (error as { response?: { data?: { message?: string } } })?.response?.data?.message
    || (error as Error)?.message

/**
 * What to request: the stacks that bring back every impacted business service, for the scenario
 * decided (or else recommended).
 *
 * Andrew, 14 Oct 2026: "When you request a stack the solution checks for any red objects ... This
 * would have to be done individually for each request so that can't be one button that does them
 * all." So each line has its own Request, which opens the same dialog as the object page - the red
 * objects, the green version, the classification range - and only then is the request grouped into
 * a recovery set, an existing one or a new one: "A recovery set should only be created when we know
 * we are going to put recovery requests in the recovery set."
 */
export function ImpactToRequest({ assessmentId, canRequest }: { assessmentId: number; canRequest: boolean }) {
    const { t } = useTranslation()
    const { data, isLoading } = useToRequest(assessmentId)
    const { data: sets = [] } = useRecoverySets()
    // What is being requested: a stack, under a scenario - the one this view is for, or a plan line's.
    const [requesting, setRequesting] = useState<{ objectId: string; scenario: number } | null>(null)
    const [grouping, setGrouping] = useState<string | null>(null)
    // All requests from this assessment go into one recovery set ("We don't cross recovery sets"):
    // the one already linked to it is offered first.
    const { data: assessments = [] } = useImpactAssessments()
    const linkedSetId = assessments.find((a) => a.id === assessmentId)?.recoverySets[0]?.id ?? null
    const setName = (id: number | null) => sets.find((s) => s.id === id)?.name ?? (id != null ? `#${id}` : null)

    const dialogs = (
        <>
            <RequestRecoveryDialog
                open={requesting != null}
                onOpenChange={(open) => { if (!open) setRequesting(null) }}
                objectID={requesting?.objectId ?? null}
                scenario={requesting?.scenario ?? null}
                onRequested={(requestId) => setGrouping(requestId)}
            />
            <GroupRequestDialog assessmentId={assessmentId} requestId={grouping} preferredSetId={linkedSetId} onClose={() => setGrouping(null)} />
        </>
    )
    const plan = <RecoveryPlanPanel assessmentId={assessmentId} canRequest={canRequest} onRequest={(objectId, scenario) => setRequesting({ objectId, scenario })} />

    if (isLoading) return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
    if (!data || data.scenarioId == null) {
        return (
            <div className="space-y-3">
                {plan}
                <p className="rounded-lg border bg-card p-4 text-sm text-muted-foreground" data-testid="to-request-none">
                    {t('recoverRequests.impact.toRequest.noScenario', 'Mark what is compromised first. Once a recovery scenario can be used, the stacks to request are listed here.')}
                </p>
                {dialogs}
            </div>
        )
    }

    return (
        <div className="space-y-3" data-testid="to-request">
            {plan}
            <div className="rounded-lg border bg-card p-4">
                <p className="text-sm">
                    {data.decided
                        ? t('recoverRequests.impact.toRequest.decided', 'Recovering by {{name}}, as decided.', { name: data.scenarioName })
                        : t('recoverRequests.impact.toRequest.recommended', 'Recovering by {{name}}, as recommended. No decision has been recorded yet.', { name: data.scenarioName })}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                    {data.impacted.length === 0
                        ? t('recoverRequests.impact.toRequest.nothingImpacted', 'No business service is impacted in this scenario.')
                        : data.oneTree
                            ? t('recoverRequests.impact.toRequest.oneTree', 'One stack covers all {{count}} impacted business services: request it once and everything comes with it.', { count: data.impacted.length })
                            : t('recoverRequests.impact.toRequest.covered', '{{covered}} of {{count}} impacted business services have a request.', { covered: data.covered, count: data.impacted.length })}
                </p>
            </div>

            {data.lines.map((line) => (
                <RequestLine
                    key={line.objectId}
                    line={line}
                    setName={setName}
                    canRequest={canRequest}
                    onRequest={() => setRequesting({ objectId: line.objectId, scenario: data.scenarioId as number })}
                    onGroup={() => line.request && setGrouping(line.request.requestId)}
                />
            ))}

            {dialogs}
        </div>
    )
}

function RequestLine({ line, setName, canRequest, onRequest, onGroup }: {
    line: ToRequestLine
    setName: (id: number | null) => string | null
    canRequest: boolean
    onRequest: () => void
    onGroup: () => void
}) {
    const { t } = useTranslation()
    const inSet = line.request?.recoverySet != null
    return (
        <div className="flex flex-wrap items-start gap-3 rounded-lg border bg-card p-4" data-testid={`to-request-${line.objectId}`}>
            <Layers className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1 space-y-1">
                <p className="font-medium">{line.name}</p>
                <p className="text-xs text-muted-foreground">
                    {t('recoverRequests.impact.toRequest.stack', 'The stack beneath it: {{count}} objects', { count: line.objects })}
                    {line.sharedObjects > 0 && ` · ${t('recoverRequests.impact.toRequest.shared', '{{count}} shared with the other requests', { count: line.sharedObjects })}`}
                </p>
                {line.covers.length > 0 && (
                    <p className="text-xs">
                        {t('recoverRequests.impact.toRequest.covers', 'Brings back: {{list}}', { list: line.covers.join(', ') })}
                    </p>
                )}
                {line.request && (
                    <p className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {inSet
                            ? t('recoverRequests.impact.toRequest.requestedInSet', 'Requested ({{id}}), in recovery set “{{set}}”', { id: line.request.requestId, set: setName(line.request.recoverySet) })
                            : t('recoverRequests.impact.toRequest.requestedNoSet', 'Requested ({{id}}), not yet in a recovery set', { id: line.request.requestId })}
                    </p>
                )}
            </div>
            {canRequest && !line.request && (
                <Button size="sm" onClick={onRequest} data-testid={`to-request-request-${line.objectId}`}>
                    <Send className="mr-1.5 h-3.5 w-3.5" />
                    {t('recoverRequests.impact.toRequest.request', 'Request')}
                </Button>
            )}
            {canRequest && line.request && !inSet && (
                <Button size="sm" variant="outline" onClick={onGroup} data-testid={`to-request-group-${line.objectId}`}>
                    <FolderPlus className="mr-1.5 h-3.5 w-3.5" />
                    {t('recoverRequests.impact.toRequest.group', 'Add to a recovery set')}
                </Button>
            )}
        </div>
    )
}

/** After a request is made: put it in a recovery set, an existing one or a new one, or leave it for now. */
export function GroupRequestDialog({ assessmentId, requestId, preferredSetId = null, onClose }: { assessmentId: number; requestId: string | null; preferredSetId?: number | null; onClose: () => void }) {
    const { t } = useTranslation()
    const { data: sets = [] } = useRecoverySets()
    const group = useGroupRequest(assessmentId)
    const [choice, setChoice] = useState<'existing' | 'new'>('existing')
    const [setId, setSetId] = useState<string>('')
    // The assessment's own recovery set, chosen already when there is one.
    useEffect(() => { if (requestId && preferredSetId != null) { setChoice('existing'); setSetId(String(preferredSetId)) } }, [requestId, preferredSetId])
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [problem, setProblem] = useState<string | null>(null)
    const mode = sets.length === 0 ? 'new' : choice
    const ready = mode === 'existing' ? setId !== '' : name.trim() !== ''

    const close = () => { setProblem(null); setSetId(''); setName(''); setDescription(''); setChoice('existing'); onClose() }
    const save = () => {
        if (!requestId) return
        setProblem(null)
        group.mutate(
            mode === 'existing'
                ? { requestId, setId: Number(setId) }
                : { requestId, newSet: { name: name.trim(), description: description.trim() || undefined } },
            { onSuccess: close, onError: (error) => setProblem(problemOf(error) ?? null) },
        )
    }

    return (
        <Dialog open={requestId != null} onOpenChange={(open) => { if (!open) close() }}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t('recoverRequests.impact.group.title', 'Put request {{id}} in a recovery set', { id: requestId ?? '' })}</DialogTitle>
                    <DialogDescription>{t('recoverRequests.impact.group.help', 'A recovery set groups the requests that are recovered together. You can also leave it for now and add it from here later.')}</DialogDescription>
                </DialogHeader>
                {sets.length > 0 && (
                    <RadioGroup value={mode} onValueChange={(v) => setChoice(v as 'existing' | 'new')} className="gap-2">
                        <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="existing" />{t('recoverRequests.impact.group.existing', 'An existing recovery set')}</label>
                        <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="new" />{t('recoverRequests.impact.group.new', 'A new recovery set')}</label>
                    </RadioGroup>
                )}
                {mode === 'existing' ? (
                    <Select value={setId} onValueChange={setSetId}>
                        <SelectTrigger aria-label={t('recoverRequests.impact.group.existing', 'An existing recovery set')} data-testid="group-set">
                            <SelectValue placeholder={t('recoverRequests.impact.group.choose', 'Choose a recovery set')} />
                        </SelectTrigger>
                        <SelectContent>
                            {sets.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                ) : (
                    <div className="space-y-3">
                        <label className="block text-sm font-medium">
                            {t('recoverRequests.impact.group.name', 'Name')}
                            <Input className="mt-1" maxLength={45} value={name} onChange={(e) => setName(e.target.value)} data-testid="group-new-name" />
                        </label>
                        <label className="block text-sm font-medium">
                            {t('recoverRequests.impact.page.description', 'Description')}
                            <Textarea className="mt-1" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
                        </label>
                    </div>
                )}
                {problem && <p role="alert" className="text-sm font-medium text-destructive">{problem}</p>}
                <DialogFooter>
                    <Button variant="outline" onClick={close}>{t('recoverRequests.impact.group.later', 'Not now')}</Button>
                    <Button onClick={save} disabled={!ready || group.isPending} data-testid="group-save">
                        {t('recoverRequests.impact.group.save', 'Add to the recovery set')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
